import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TourButton from '../../TourButton/TourButton';
import { getFoodModalSteps } from '../../../tours/foodModal';
import { useMediaQuery } from '@mui/material';
import axios from 'axios';
import { useNotification } from '../../notifications/useNotification';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import './FoodModal.css';
import { capitalizeFirst } from '../../../utils/functions';

/* ── Constants ───────────────────────────────────────────────────────────── */

const CATEGORIES = [
    'poultry',
    'meat',
    'fish & seafood',
    'dairy',
    'eggs',
    'grains',
    'legumes',
    'vegetables',
    'fruits',
    'healthy fats',
    'nuts & seeds',
    'condiments',
    'beverages',
    'supplements',
    'other',
];

const UNITS = ['gr', 'ml', 'piece', 'cup', 'tbsp', 'tsp'];

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * FoodModal — Add or Edit a food item.
 *
 * Props:
 *   setShowModal  {Function}  — closes the modal
 *   onSave        {Function}  — called after a successful save (refresh list, etc.)
 *   food          {Object?}   — if provided, modal runs in edit mode and pre-fills the form
 */
const FoodModal = ({ setShowModal, onSave, food }) => {
    const { t, i18n } = useTranslation();
    const { showSuccess, showError } = useNotification();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [loading, setLoading] = useState(false);
    const [errors, setErrors]   = useState({});

    const [formData, setFormData] = useState({
        nameEn:       food?.name?.en       ?? '',
        nameIt:       food?.name?.it       ?? '',
        category:     (typeof food?.category === 'object' ? food?.category?.en : food?.category) ?? '',
        unit:         food?.unit           ?? 'gr',
        quantity:     food?.quantity        ?? '',
        kcal:         food?.kcal           ?? '',
        fats:         food?.fats           ?? '',
        satured_fats: food?.satured_fats   ?? '',
        carbs:        food?.carbs          ?? '',
        sugars:       food?.sugars         ?? '',
        proteins:     food?.proteins       ?? '',
        fibers:       food?.fibers         ?? '',
        salt:         food?.salt           ?? '',
    });

    /* ── Handlers ─────────────────────────────────────────────────────── */

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error on change
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nameEn.trim()) newErrors.nameEn   = t('fm-error-name-en-required');
        if (!formData.nameIt.trim()) newErrors.nameIt   = t('fm-error-name-it-required');
        if (!formData.category)      newErrors.category = t('fm-error-category-required');
        if (!formData.kcal && formData.kcal !== 0) newErrors.kcal = t('fm-error-kcal-required');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const toNumberOrZero = (val) => {
        if (val === '' || val === null || val === undefined) return 0;
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            const tEn = i18n.getFixedT('en');
            const tIt = i18n.getFixedT('it');

            const payload = {
                name: {
                    en: formData.nameEn.trim(),
                    it: formData.nameIt.trim(),
                },
                category: {
                    en: tEn(`fm-category-names.${formData.category}`),
                    it: tIt(`fm-category-names.${formData.category}`),
                },
                unit:         formData.unit,
                quantity:     toNumberOrZero(formData.quantity),
                kcal:         toNumberOrZero(formData.kcal),
                fats:         toNumberOrZero(formData.fats),
                satured_fats: toNumberOrZero(formData.satured_fats),
                carbs:        toNumberOrZero(formData.carbs),
                sugars:       toNumberOrZero(formData.sugars),
                proteins:     toNumberOrZero(formData.proteins),
                fibers:       toNumberOrZero(formData.fibers),
                salt:         toNumberOrZero(formData.salt),
            };

            if (food) {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/foods/${food._id}`,
                    payload,
                    { withCredentials: true }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/foods`,
                    payload,
                    { withCredentials: true }
                );
            }

            await onSave();
            showSuccess(food ? t('fm-success-edit') : t('fm-success-add'));
            handleClose();
        } catch (error) {
            showError(food ? t('fm-error-edit') : t('fm-error-add'));
            console.error('FoodModal error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setShowModal(false);
    };

    /* ── Render ───────────────────────────────────────────────────────── */

    return (
        <>
            <LoadingOverlay open={loading} />

            <div className="fm-overlay">
                <div className="fm-modal" data-tour="fm-modal">

                    {/* ── Header ── */}
                    <div className="fm-header">
                        <div className="fm-header-text">
                            <h2 className="fm-title">
                                {food ? t('fm-title-edit') : t('fm-title-add')}
                            </h2>
                            <p className="fm-subtitle">
                                {food ? t('fm-subtitle-edit') : t('fm-subtitle-add')}
                            </p>
                        </div>
                        <div className="fm-tutorial-btn">
                            <TourButton steps={getFoodModalSteps(t)} iconOnly={isMobile} />
                        </div>
                        <button className="fm-close-btn" onClick={handleClose} aria-label="Close">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className="fm-body">

                        {/* Names section */}
                        <div className="fm-section" data-tour="fm-names">
                            <span className="fm-section-label">{t('fm-section-names')}</span>
                            <div className="fm-row">
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-name-en')}</label>
                                    <input
                                        className={`fm-input${errors.nameEn ? ' error' : ''}`}
                                        type="text"
                                        name="nameEn"
                                        value={formData.nameEn}
                                        onChange={handleInputChange}
                                        placeholder={t('fm-name-en-placeholder')}
                                    />
                                    {errors.nameEn && <span className="fm-error">{errors.nameEn}</span>}
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-name-it')}</label>
                                    <input
                                        className={`fm-input${errors.nameIt ? ' error' : ''}`}
                                        type="text"
                                        name="nameIt"
                                        value={formData.nameIt}
                                        onChange={handleInputChange}
                                        placeholder={t('fm-name-it-placeholder')}
                                    />
                                    {errors.nameIt && <span className="fm-error">{errors.nameIt}</span>}
                                </div>
                            </div>
                        </div>

                        {/* General info section */}
                        <div className="fm-section" data-tour="fm-general">
                            <span className="fm-section-label">{t('fm-section-general')}</span>
                            <div className="fm-row fm-row-3">
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-category')}</label>
                                    <select
                                        className={`fm-select${errors.category ? ' error' : ''}`}
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{t('fm-category-placeholder')}</option>
                                        {CATEGORIES.map(c => (
                                            <option key={c} value={c}>{capitalizeFirst(t(`fm-category-names.${c}`))}</option>
                                        ))}
                                    </select>
                                    {errors.category && <span className="fm-error">{errors.category}</span>}
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-unit')}</label>
                                    <select
                                        className="fm-select"
                                        name="unit"
                                        value={formData.unit}
                                        onChange={handleInputChange}
                                    >
                                        {UNITS.map(u => (
                                            <option key={u} value={u}>{u}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-quantity')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                        placeholder="100"
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Nutritional values section */}
                        <div className="fm-section" data-tour="fm-macros">
                            <span className="fm-section-label">{t('fm-section-macros')}</span>

                            {/* Calories */}
                            <div className="fm-field fm-field-kcal">
                                <label className="fm-label">
                                    {t('fm-kcal')}
                                    <span className="fm-required">*</span>
                                </label>
                                <input
                                    className={`fm-input${errors.kcal ? ' error' : ''}`}
                                    type="number"
                                    name="kcal"
                                    value={formData.kcal}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                    min="0"
                                />
                                {errors.kcal && <span className="fm-error">{errors.kcal}</span>}
                            </div>

                            {/* Fat + Saturated Fat */}
                            <div className="fm-row">
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-fat')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="fats"
                                        value={formData.fats}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-saturated-fat')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="satured_fats"
                                        value={formData.satured_fats}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                            </div>

                            {/* Carbs + Sugars */}
                            <div className="fm-row">
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-carbs')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="carbs"
                                        value={formData.carbs}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-sugars')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="sugars"
                                        value={formData.sugars}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                            </div>

                            {/* Proteins + Fibers + Salt */}
                            <div className="fm-row fm-row-3">
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-proteins')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="proteins"
                                        value={formData.proteins}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-fibers')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="fibers"
                                        value={formData.fibers}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="fm-field">
                                    <label className="fm-label">{t('fm-salt')}</label>
                                    <input
                                        className="fm-input"
                                        type="number"
                                        name="salt"
                                        value={formData.salt}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                            </div>

                        </div>

                    </div>

                    {/* ── Footer ── */}
                    <div className="fm-footer" data-tour="fm-footer">
                        <button className="fm-btn fm-btn-cancel" onClick={handleClose}>
                            {t('Cancel')}
                        </button>
                        <button className="fm-btn fm-btn-save" onClick={handleSubmit}>
                            {t('Save')}
                        </button>
                    </div>

                </div>
            </div>
        </>
    );
};

export default FoodModal;
